import { Select, type SelectOption } from '@/components/ui/Select'
import type { AssetFilterValue, DateRange, TimePeriod } from '@/lib/submissionFilter'
import { ALL_ASSETS } from '@/lib/submissionFilter'
import { TimePeriodChips } from './TimePeriodChips'
import { DateRangePicker } from './DateRangePicker'

interface SubmissionFilterBarProps {
  assetId: AssetFilterValue
  assetOptions: readonly SelectOption[]
  period: TimePeriod
  range: DateRange
  onAssetChange: (assetId: AssetFilterValue) => void
  onPeriodChange: (period: TimePeriod) => void
  onRangeChange: (range: DateRange) => void
}

/**
 * Always-visible inline filter for the My Submissions page: an asset dropdown
 * over a segmented time-period control, with the custom date-range panel
 * revealed only for the "Custom range" period (design variant C).
 */
export function SubmissionFilterBar({
  assetId,
  assetOptions,
  period,
  range,
  onAssetChange,
  onPeriodChange,
  onRangeChange,
}: SubmissionFilterBarProps): JSX.Element {
  const selectedAsset = assetOptions.find((option) => option.id === assetId)
  const triggerLabel = selectedAsset?.label ?? 'All assets'

  return (
    <div className="space-y-3">
      <Select<AssetFilterValue>
        value={assetId}
        onChange={onAssetChange}
        options={assetOptions}
        triggerLabel={triggerLabel}
        ariaLabel="Filter by asset"
      />
      <div>
        <TimePeriodChips value={period} onChange={onPeriodChange} />
        {period === 'custom' && <DateRangePicker range={range} onChange={onRangeChange} />}
      </div>
    </div>
  )
}

export const ALL_ASSETS_OPTION: SelectOption = { id: ALL_ASSETS, label: 'All assets' }
